
import Landing from '@/components/Landing';
import { getNestedString, useConfig } from '@/utils/useConfig';
import FuseLoading from '@fuse/core/FuseLoading';
import Link from '@fuse/core/Link';
import useNavigate from '@fuse/hooks/useNavigate';
import useQuery from '@fuse/hooks/useQuery';
import { CircularProgress } from '@mui/material';
import Paper from '@mui/material/Paper';
import { useCallback, useEffect, useRef, useState } from 'react';
import JwtSignUpTab from './tabs/JwtSignUpTab';
import { Helmet } from 'react-helmet';
import { TermsPrivacyFooter } from '@/components';


/**
 * The sign up page.
 */
function SignUpPage() {
	const [loading, setLoading] = useState(false);
	const [accessToken, setAccessToken] = useState('');
	const usedCode = useRef(false);
	const [social, setSocial] = useState(null);

	const query = useQuery();
	const navigate = useNavigate();


	const { config, loading: _loading } = useConfig('auth_pages_config');


	const handleGoogleAuth = useCallback((code: string): void => {
		if (usedCode.current) {
			return;
		}

		usedCode.current = true;
		setAccessToken(code);

		const request: { token: string; provider: string; local?: string; } = {
			token: code,
			provider: 'google',
		};

		if (import.meta.env.DEV) {
			request.local = 'true';
		}

		setSocial(request);



	}, []);

	/**
	 * Runs when the page loads
	 */
	useEffect(() => {
		/**
		 * If the token is not valid, it should redirect them to the sign in page
		 */

		const urlParams = new URLSearchParams(globalThis.location.search);
		const code = urlParams.get('code');

		if (code) {
			handleGoogleAuth(code);
		} else if (!query.has('token')) {
			navigate('/sign-in');
		}


		/**
		 * If the token is there, it needs to be valid, if not, send them back to the sign-in screen
		 */
		if (query.has('token')) {
			const urlToken = query.get('token');
			const configToken = getNestedString(config, 'signUp.signUpToken');

			// Prevent running logic until config is ready
			if (!configToken) return;

			if (!urlToken || urlToken !== configToken) {
				navigate('/sign-in');
			}
		}
	}, [query, navigate, config, handleGoogleAuth]);

	if (_loading) {
		return <FuseLoading />;
	}

	return (
		<>
			<Helmet>
				<meta charSet="utf-8" />
				<title>{getNestedString(config, 'signUp.metadata.title')}</title>
				<meta
					name="description"
					content={getNestedString(config, 'signUp.metadata.description')}
				/>
				<meta
					name="keywords"
					content={getNestedString(config, 'signUp.metadata.keywords')}
				/>
			</Helmet>
	
		<div className="flex min-w-0 flex-1 flex-col items-center sm:flex-row sm:justify-center md:items-start md:justify-start">
			<Paper className="h-full w-full px-16 py-8 ltr:border-r-1 rtl:border-l-1 sm:h-auto sm:w-auto sm:rounded-xl sm:p-48 sm:shadow md:flex md:h-full md:w-1/2 md:items-center md:justify-end md:rounded-none md:p-64 md:shadow-none">
				<div className="mx-auto w-full max-w-320 sm:mx-0 sm:w-320">
					<img
						className="mx-auto"
						src={getNestedString(config, 'image')}
						alt="logo"
					/>

					<h1 className="mt-32 text-4xl font-extrabold leading-tight tracking-tight">
						Sign up
					</h1>

					<div className="mt-2 flex items-baseline font-medium">
						<p>Already have an account?</p>
						<Link
							className="ml-4"
							to="/sign-in"
						>
							Sign in
						</Link>
					</div>

					{loading ? <div className='py-24 w-full flex justify-center '>
						<CircularProgress />
					</div> : <JwtSignUpTab config={config} social={social} />}

					<TermsPrivacyFooter shortName={getNestedString(config, 'shortName')} />

				</div>
			</Paper>

			<Landing
				title={getNestedString(config, 'landingTitle')}
				description={getNestedString(config, 'signUp.landingDescription')}
			/>
		</div>
</>
	);
}

export default SignUpPage;
